import { Injectable } from "@angular/core";
import { initializeApp } from "firebase/app";
import { Auth, getAuth, onAuthStateChanged, User } from "firebase/auth";
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable
} from "rxjs";
import { environment } from "src/environments/environment";

export interface AuthUser extends User {}

@Injectable({
  providedIn: "root",
})
export class FirebaseService {
  readonly #auth: Auth;
  readonly #userSubject = new BehaviorSubject<AuthUser | null>(null);
  readonly #authReady = new BehaviorSubject<boolean>(false);
  private readonly authReady$ = this.#authReady.asObservable();
  /** @ignore */
  constructor() {
    initializeApp(environment.firebaseConfig);

    this.#auth = getAuth();

    onAuthStateChanged(this.#auth, (user) => {
      this.#userSubject.next(user);
      this.#authReady.next(true);
    });
  }

  /**
   * Synchronous getter for current user
   */
  get currentUser(): AuthUser | null {
    return this.#userSubject.value;
  }

  /**
   * Observable of current user,
   * Only returns a value when `onAuthStateChanged`
   * has triggered
   */
  get user$(): Observable<AuthUser | null> {
    return combineLatest([
      this.authReady$,
      this.#userSubject.asObservable(),
    ]).pipe(
      filter(([ready, _]) => ready),
      map(([_, user]) => user)
    );
  }

  /**
   * Observable of login state,
   * Only returns a value when `onAuthStateChanged`
   * has triggered
   */
  get isLoggedIn$(): Observable<boolean> {
    return combineLatest([
      this.authReady$,
      this.#userSubject.asObservable(),
    ]).pipe(
      filter(([ready, _]) => ready),
      map(([_, user]) => user !== null)
    );
  }

  /**
   * Get ID token for current user
   *
   * Note: Uses synchronous user getter
   */
  async getToken(): Promise<string | null> {
    const user = this.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  }

  /**
   * Get Firebase Auth
   * @returns {Auth} auth object
   */
  async signOut(): Promise<void> {
    await this.#auth.signOut();
  }
}
